import { useState } from "react";
import { Button, Dialog, FilePicker } from "@rio-cloud/rio-uikit";
import ErrorDialog from "../../../components/dialogs/ErrorDialog";
import ConfirmDialog from "../../../components/dialogs/ConfirmDialog";
import { formatFileSize } from "../../../utils/imageSizeUtils";
import { FileAttachment, FileAttachmentSimple } from "../../../api/tas";
import { suspendPageLoad } from "../../../routes/suspendPageLoad";

const PdfPreview = suspendPageLoad(() => import("./PdfPreview"));

interface IUploadProps {
  addFileToUpload: (files: File[]) => void;
  removeFileToUpload: (fileName: string) => void;
}

const Upload = (props: IUploadProps) => {
  const [files, setFiles] = useState<FileAttachmentSimple[]>([]);
  const [uploadErrorMessage, setUploadErrorMessage] = useState<
    string | undefined
  >(undefined);

  return (
    <div>

      <div className="padding-15 border rounded">
        <div className="margin-bottom-20">
          <span className="text-medium">Novo arquivo</span>
        </div>
        <FilePicker
          displayMode="dropzone"
          className=""
          onPick={async (acceptedFiles: FileList | null) => {
            const maxSize = 20 * 1024 * 1024;
            const filePicked = acceptedFiles?.[0];

            if (!filePicked) return;

            if (filePicked.size > maxSize) {
              return setUploadErrorMessage(
                "O arquivo selecionado excede o limite de 20MB. Escolha um arquivo menor."
              );
            }

            const file: FileAttachmentSimple = {
              fileId: crypto.randomUUID(),
              key: '',
              name: filePicked.name,
              url: URL.createObjectURL(filePicked),
              fileType: filePicked.type,            
              fileSize: filePicked.size,
              updatedAt: new Date().toISOString(),
            };
            
            setFiles((prev) => [...prev, file])

            if (acceptedFiles?.[0]) {
              props.addFileToUpload(Array.from(acceptedFiles));
            }
          }}
          multiple={true}
        //   accept={accept}
        >
          {dropArea}
        </FilePicker>
      </div>

      <div className="width-100pct border padding-15 margin-top-15 rounded">
        <div className="margin-bottom-20">
          <span className="text-medium">Arquivos para enviar</span>
        </div>

        {files?.length > 0 ? (
          files.map((file, index) => (
            <FilePreview
              key={index}
              file={file}
              path={file.url}
              remove={() => {
                setFiles((prevFiles) =>
                  (prevFiles ?? []).filter((f) => f.name !== file.name)
                );
                props.removeFileToUpload(file.name);
              }}
              disabled={false}
            />
          ))
        ) : (
          <div className="text-color-dark text-italic">Nenhum arquivo</div>
        )}
      </div>

      <ErrorDialog
        show={!!uploadErrorMessage}
        title={"Erro ao fazer upload"}
        body={uploadErrorMessage ?? ""}
        onClose={() => setUploadErrorMessage(undefined)}
      />
    </div>
  );
};

const dropArea = ({ isDragActive }: { isDragActive: boolean }) => (
  <div className={`rounded border border-style-dashed border-color-gray bg-lightest text-size-14 text-center text-color-darker opacity-50 padding-25 cursor-pointer`}>
    <span className="rioglyph rioglyph-files text-size-200pct opacity-50 margin-top-20" />
    <div className="margin-top-10">
      {isDragActive
        ? "Jogue os arquivos aqui ..."
        : "Arraste seus arquivos para cá ou clique para selecionar no computador"}
    </div>
  </div>
);

const FilePreview = ({
  file,
  path,
  remove,
  disabled,
}: {
  file: FileAttachmentSimple | undefined;
  path: string;
  remove: () => void;
  disabled: boolean;
}) => {
  const [deleteFileDialog, setDeleteFileDialog] = useState<boolean>(false);
  const [previewDialog, setPreviewDialog] = useState<boolean>(false);

  return (
    <>
      <div
        className="panel panel-default cursor-pointer"
        onClick={async () => {
          if (file) setPreviewDialog(true);
        }}
      >
        <div className="panel-body">
          {file && (
            <div className="flex">
              <div className="flex justify-between w-full">
                <div>
                  <div>
                    <b>{`${file?.name}`}</b>
                  </div>
                  <ul className='margin-0'>
                    <li>
                      <label htmlFor="fileType" className='margin-right-10'>type: </label>
                      <i id="fileType">{`${file?.fileType}`}</i>
                    </li>
                    <li>
                      <label htmlFor="fileSize" className='margin-right-10'>size: </label>
                      <i id="fileSize">{`${formatFileSize(file?.fileSize)}`}</i>
                    </li>
                  </ul>
                </div>
                {!disabled && (
                  <div
                    onClick={(e) => {
                      e.stopPropagation();
                      setDeleteFileDialog(true);
                    }}
                  >
                    <span className="rioglyph rioglyph-remove" />
                  </div>
                )}
              </div>
            </div>
          )}
          {!file && (
            <div className="text-color-dark text-italic">Nenhum arquivo</div>
          )}
        </div>
      </div>

      <ConfirmDialog
        show={deleteFileDialog}
        title="Remover arquivo"
        body="Deseja realmente remover o arquivo?"
        onClose={() => setDeleteFileDialog(false)}
        onConfirm={async () => {
          if (file) {
            remove();
            setDeleteFileDialog(false);
          }
        }}
      />

      <Dialog
        show={previewDialog}
        title={file?.name}
        onEsc={() => setPreviewDialog(false)}
        onClose={() => setPreviewDialog(false)}
        bsSize="lg"
        body={
          <div className="flex justify-center">
            {file?.fileType.startsWith("image/") ? (
              <img
                src={file?.url}
                alt={file?.name}
                className="max-w-full max-h-[600px]"
              />
            ) : file?.fileType === "application/pdf" ? (
              <PdfPreview url={file?.url} />
            ) : (
              <div className="text-color-dark">
                Pré-visualização indisponível para este tipo de arquivo.
              </div>
            )}
          </div>
        }
        footer={
          <>
            <div className="display-flex justify-content-end">
              <Button
                bsStyle="default"
                onClick={() => setPreviewDialog(false)}
              >
                Fechar
              </Button>
            </div>
          </>
        }
      />
    </>
  );
};

export default Upload;
